/**
 * Web MIDI plumbing: device access, message routing to bindings/actions/modulation sources,
 * MIDI learn, and MIDI clock → tempo. (Not available in Safari.)
 */
import {
  MidiClock,
  SoftTakeover,
  parseMidi,
  sameTrigger,
  triggerOf,
  type MidiBinding,
  type MidiMessage,
  type MidiTarget,
} from './midi';
import type { SyncKV } from './storage';

const BINDINGS_KEY = 'glowtrope.midi.bindings.v1';

export interface MidiHost {
  /** Current normalized (0..1) value of a parameter path, for soft takeover. */
  paramValue(path: string): number | null;
  setParam(path: string, normalized: number): void;
  runAction(action: string, arg: number | undefined, down: boolean): void;
  /** Every CC is also a modulation source. */
  ccSource(source: string, value: number): void;
  clockBeat(perfMs: number, bpm: number): void;
  clockStop(): void;
  onChange(): void;
}

export class MidiController {
  enabled = false;
  supported = typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
  inputs: string[] = [];
  bindings: MidiBinding[];
  learning: MidiTarget | null = null;
  lastMessage = '';
  clockBpm: number | null = null;
  /** CC sources seen this session (offered as modulation sources). */
  seenCC: string[] = [];
  private access: MIDIAccess | null = null;
  private readonly clock = new MidiClock();
  private readonly takeover = new SoftTakeover();
  private clockTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly host: MidiHost,
    private readonly kv: SyncKV,
  ) {
    this.bindings = sanitizeBindings(kv.get(BINDINGS_KEY));
  }

  async enable(): Promise<boolean> {
    if (!this.supported) return false;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch {
      return false;
    }
    this.enabled = true;
    this.access.onstatechange = () => this.refresh();
    this.refresh();
    return true;
  }

  disable(): void {
    if (this.access) for (const input of this.access.inputs.values()) input.onmidimessage = null;
    this.enabled = false;
    this.inputs = [];
    this.host.onChange();
  }

  private refresh(): void {
    if (!this.access) return;
    this.inputs = [];
    for (const input of this.access.inputs.values()) {
      this.inputs.push(input.name ?? 'MIDI input');
      input.onmidimessage = (e) => {
        if (e.data) this.handle(e.data, e.timeStamp);
      };
    }
    this.host.onChange();
  }

  learn(target: MidiTarget | null): void {
    this.learning = target;
    this.host.onChange();
  }

  unbind(id: string): void {
    this.bindings = this.bindings.filter((b) => b.id !== id);
    this.save();
  }

  /** A parameter changed elsewhere; the knob must pick it up again. */
  releaseTakeover(): void {
    this.takeover.release();
  }

  handle(data: ArrayLike<number>, timeStamp: number): void {
    const msg = parseMidi(data);
    if (!msg) return;
    if (
      msg.type === 'clock' ||
      msg.type === 'start' ||
      msg.type === 'continue' ||
      msg.type === 'stop'
    ) {
      this.handleClock(msg, timeStamp);
      return;
    }
    this.lastMessage = describe(msg);
    if (msg.type === 'cc') {
      const source = `midi.cc.${msg.channel}.${msg.number}`;
      this.host.ccSource(source, msg.value / 127);
      if (!this.seenCC.includes(source)) this.seenCC = [...this.seenCC, source].slice(-32);
    }
    const trigger = triggerOf(msg);
    if (trigger && this.learning) {
      const kind = this.learning.kind;
      if (
        (kind === 'param' && trigger.kind === 'cc') ||
        (kind === 'action' && trigger.kind !== 'cc')
      ) {
        this.bindings = this.bindings.filter((b) => !sameTrigger(b.trigger, trigger));
        this.bindings.push({ id: `b${Date.now().toString(36)}`, trigger, target: this.learning });
        this.learning = null;
        this.save();
        return;
      }
    }
    const isUp = msg.type === 'noteoff';
    const upTrigger = isUp
      ? { kind: 'note' as const, channel: msg.channel, number: msg.number }
      : null;
    for (const b of this.bindings) {
      if (trigger && sameTrigger(b.trigger, trigger)) {
        if (b.target.kind === 'param' && msg.type === 'cc') {
          const v = msg.value / 127;
          const cur = this.host.paramValue(b.target.path);
          if (cur === null || this.takeover.accept(b.target.path, v, cur))
            this.host.setParam(b.target.path, v);
        } else if (b.target.kind === 'action') {
          this.host.runAction(b.target.action, b.target.arg, true);
        }
      } else if (upTrigger && sameTrigger(b.trigger, upTrigger) && b.target.kind === 'action') {
        this.host.runAction(b.target.action, b.target.arg, false);
      }
    }
    this.host.onChange();
  }

  private handleClock(msg: MidiMessage, t: number): void {
    if (msg.type === 'start' || msg.type === 'continue') this.clock.start();
    if (msg.type === 'stop') {
      this.clock.stop();
      this.clockBpm = null;
      this.host.clockStop();
      this.host.onChange();
      return;
    }
    if (msg.type !== 'clock') return;
    const beat = this.clock.tick(t);
    if (beat) {
      const changed = this.clockBpm === null || Math.abs(this.clockBpm - beat.bpm) > 0.5;
      this.clockBpm = beat.bpm;
      this.host.clockBeat(beat.beatTimeMs, beat.bpm);
      if (changed) this.host.onChange();
    }
    if (this.clockTimer) clearTimeout(this.clockTimer);
    this.clockTimer = setTimeout(() => {
      this.clockBpm = null;
      this.host.clockStop();
      this.host.onChange();
    }, 1500);
  }

  private save(): void {
    this.kv.set(BINDINGS_KEY, this.bindings);
    this.host.onChange();
  }
}

function describe(m: MidiMessage): string {
  switch (m.type) {
    case 'cc':
      return `CC ${m.number} = ${m.value} (ch ${m.channel})`;
    case 'noteon':
      return `Note ${m.number} on (ch ${m.channel})`;
    case 'noteoff':
      return `Note ${m.number} off (ch ${m.channel})`;
    case 'program':
      return `Program ${m.number} (ch ${m.channel})`;
    default:
      return m.type;
  }
}

function sanitizeBindings(input: unknown): MidiBinding[] {
  if (!Array.isArray(input)) return [];
  return input.filter(
    (b): b is MidiBinding =>
      !!b &&
      typeof b.id === 'string' &&
      !!b.trigger &&
      ['cc', 'note', 'program'].includes(b.trigger.kind) &&
      !!b.target &&
      (b.target.kind === 'param' || b.target.kind === 'action'),
  );
}
